import { MessageRoute } from '../../types';

interface MessageRouteFlowDiagramProps {
  route: MessageRoute;
}

export default function MessageRouteFlowDiagram({ route }: MessageRouteFlowDiagramProps) {
  const conditionParts: string[] = [];
  if (route.entity) conditionParts.push(`entity=${route.entity}`);
  if (route.operations.length > 0) {
    conditionParts.push(`op in [${route.operations.join(', ')}]`);
  }
  if (route.source) conditionParts.push(`source=${route.source}`);
  const conditionLabel = conditionParts.length > 0 ? conditionParts.join(' AND ') : 'all messages';

  const mappingFieldCount = Object.keys(route.inputMapping).length;
  const rightArrowLabel = `${route.endpointMethod} · ${mappingFieldCount} field${mappingFieldCount !== 1 ? 's' : ''} mapped`;

  const cooldownSeconds = Math.round(route.cooldownMs / 1000);

  return (
    <div className="w-full overflow-x-auto py-2">
      <div className="flex items-center gap-0 min-w-[600px]">
        {/* Channel Source Node */}
        <div className="flex-1 flex flex-col items-center">
          <div className="w-full border-2 border-blue-300 dark:border-blue-600 rounded-xl bg-blue-50 dark:bg-blue-950 p-4 text-center shadow-sm">
            <div className="text-xs font-semibold text-blue-500 dark:text-blue-400 uppercase tracking-wide mb-1">
              Channel Source
            </div>
            <div className="font-mono text-sm font-bold text-blue-800 dark:text-blue-200 break-all">
              {route.channelPattern}
            </div>
            {(route.entity || route.operations.length > 0) && (
              <div className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                {route.entity && <span className="mr-1">entity: {route.entity}</span>}
                {route.operations.length > 0 && (
                  <span>[{route.operations.join(', ')}]</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Left Arrow */}
        <div className="flex flex-col items-center mx-2 shrink-0" style={{ minWidth: '80px' }}>
          <div className="text-xs text-gray-500 dark:text-gray-400 text-center mb-1 leading-tight max-w-[80px]">
            {conditionLabel}
          </div>
          <div className="flex items-center w-full">
            <div className="flex-1 h-0.5 bg-gray-400 dark:bg-gray-500" />
            <div
              className="w-0 h-0"
              style={{
                borderTop: '5px solid transparent',
                borderBottom: '5px solid transparent',
                borderLeft: '8px solid',
                borderLeftColor: 'rgb(156 163 175)',
              }}
            />
          </div>
        </div>

        {/* Message Route Node */}
        <div className="flex-1 flex flex-col items-center">
          <div className="w-full border-2 border-indigo-300 dark:border-indigo-600 rounded-xl bg-indigo-50 dark:bg-indigo-950 p-4 text-center shadow-sm">
            <div className="text-xs font-semibold text-indigo-500 dark:text-indigo-400 uppercase tracking-wide mb-1">
              Message Route
            </div>
            <div className="text-sm font-bold text-indigo-800 dark:text-indigo-200 truncate">
              {route.name}
            </div>
            <div className="mt-1.5 flex items-center justify-center gap-2 flex-wrap">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                priority {route.priority}
              </span>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  route.active
                    ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                {route.active ? 'active' : 'inactive'}
              </span>
            </div>
            {route.cooldownMs > 0 && (
              <div className="mt-1 text-xs text-indigo-600 dark:text-indigo-400">
                cooldown: {cooldownSeconds}s
              </div>
            )}
          </div>
        </div>

        {/* Right Arrow */}
        <div className="flex flex-col items-center mx-2 shrink-0" style={{ minWidth: '80px' }}>
          <div className="text-xs text-gray-500 dark:text-gray-400 text-center mb-1 leading-tight max-w-[80px]">
            {rightArrowLabel}
          </div>
          <div className="flex items-center w-full">
            <div className="flex-1 h-0.5 bg-gray-400 dark:bg-gray-500" />
            <div
              className="w-0 h-0"
              style={{
                borderTop: '5px solid transparent',
                borderBottom: '5px solid transparent',
                borderLeft: '8px solid',
                borderLeftColor: 'rgb(156 163 175)',
              }}
            />
          </div>
        </div>

        {/* Target Endpoint Node */}
        <div className="flex-1 flex flex-col items-center">
          <div className="w-full border-2 border-emerald-300 dark:border-emerald-600 rounded-xl bg-emerald-50 dark:bg-emerald-950 p-4 text-center shadow-sm">
            <div className="text-xs font-semibold text-emerald-500 dark:text-emerald-400 uppercase tracking-wide mb-1">
              Target Endpoint
            </div>
            <div className="font-mono text-sm font-bold text-emerald-800 dark:text-emerald-200 break-all">
              {route.endpointPath}
            </div>
            <div className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">
              {route.endpointMethod}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
